// ~/composables/useFacebookEvents.client.ts
import type {CartInterface, CartItemInterface} from "~~/server/types/CartInterface";
import type {ProductListInterface} from "~~/server/types/Products";
import type {OrderInterface} from "~~/server/types/OrderInterface";

type MetaEventSender = (...args: unknown[]) => boolean
let pixelSender: MetaEventSender | undefined

export function configureMetaEventSender(sender: MetaEventSender) {
    pixelSender = sender
}

export function useFacebookEvents() {
    const proxy = { fbq: (...args: unknown[]) => { return pixelSender?.(...args) ?? false } };

    const { consent } = useCookieConsent();
    const settings = useSettingsStore();
    const ready = () => consent.value.decided && consent.value.marketing
        && !!settings.settings.facebook_pixel && !!pixelSender;

    const viewItem = (product: ProductListInterface, eventId?: string) => {
        if (!ready()) return;
        return proxy.fbq("track", "ViewContent", {
            currency: settings.settings.currency || "EUR",
            value: Number(product.price ?? 0),
            content_type: "product",
            content_ids: [String(product.id)],
            contents: [{ id: String(product.id) }],
        }, eventId ? { eventID: eventId } : undefined);
    };

    const addToCart = (cartItem: CartItemInterface, eventId?: string) => {
        if (!ready()) return;
        return proxy.fbq("track", "AddToCart", {
            currency: settings.settings.currency || "EUR",
            value: Number(cartItem.total ?? 0),
            content_type: "product",
            content_ids: [String(cartItem.product_id)],
            contents: [{ id: String(cartItem.product_id), quantity: Number(cartItem.count ?? 0) }],
        }, eventId ? { eventID: eventId } : undefined);
    };

    const removeFromCart = (cartItem: CartItemInterface) => {
        if (!ready()) return;
        return proxy.fbq("trackCustom", "RemoveFromCart", {
            currency: settings.settings.currency || "EUR",
            value: Number(cartItem.total ?? 0),
            content_type: "product",
            content_ids: [String(cartItem.product_id)],
            contents: [{ id: String(cartItem.product_id), quantity: Number(cartItem.count ?? 0) }],
        });
    };

    const viewCart = (cart: CartInterface) => {
        if (!ready()) return;
        return proxy.fbq("trackCustom", "ViewCart", {
            currency: settings.settings.currency || "EUR",
            value: cart.products.reduce((acc, cur) => acc + Number(cur.total ?? 0), 0),
            content_type: "product",
            content_ids: cart.products.map(i => String(i.product_id)),
            contents: cart.products.map(i => ({ id: String(i.product_id), quantity: Number(i.count ?? 0) })),
        });
    };

    const beginCheckout = (cart: CartInterface, eventId?: string) => {
        if (!ready()) return;
        return proxy.fbq("track", "InitiateCheckout", {
            currency: settings.settings.currency || "EUR",
            value: cart.products.reduce((acc, cur) => acc + Number(cur.total ?? 0), 0),
            content_type: "product",
            content_ids: cart.products.map(i => String(i.product_id)),
            contents: cart.products.map(i => ({ id: String(i.product_id), quantity: Number(i.count ?? 0) })),
        }, eventId ? { eventID: eventId } : undefined);
    };

    const purchase = (cart: CartInterface | null, order: OrderInterface, eventId?: string) => {
        if (!ready()) return;
        const purchase = order.tracking?.purchase;
        const items = purchase?.contents ?? (cart?.products ?? []).map(i => ({ id: String(i.product_id), quantity: Number(i.count ?? 0) }));
        return proxy.fbq("track", "Purchase", {
            currency: purchase?.currency ?? (order.currency || "EUR"),
            value: Number(purchase?.value ?? order.preorder?.original_total ?? order.total ?? 0),
            content_type: "product",
            content_ids: items.map(i => i.id),
            contents: items.map(i => ({ id: i.id, quantity: i.quantity })),
        }, eventId ? { eventID: eventId } : undefined);
    };

    const login = (method: string) => {
        if (!ready()) return;
        return proxy.fbq("trackCustom", "Login", { method });
    };

    const search = (query: string, eventId?: string) => {
        if (!ready()) return;
        return proxy.fbq("track", "Search", { search_string: query }, eventId ? { eventID: eventId } : undefined);
    };

    const addToFavorites = (product: ProductListInterface, eventId?: string) => {
        if (!ready()) return;
        return proxy.fbq("track", "AddToWishlist", {
            currency: settings.settings.currency || "EUR",
            value: Number(product.price ?? 0),
            content_type: "product",
            content_ids: [String(product.id)],
            contents: [{ id: String(product.id) }],
        }, eventId ? { eventID: eventId } : undefined);
    };
    const callToAction=(product: ProductListInterface, eventId?: string)=>{
        if (!ready()) return;
        return proxy.fbq("track", "Contact",{
            currency: settings.settings.currency || "EUR",
            value: Number(product.price ?? 0),
            content_type: "product",
            content_ids: [String(product.id)],
            contents: [{ id: String(product.id) }],
        }, eventId ? { eventID: eventId } : undefined);
    }

    const event = (name: string, properties: Record<string, unknown> = {}, eventId?: string, custom = false) => {
        if (!ready()) return false
        return proxy.fbq(custom ? 'trackCustom' : 'track', name, properties, eventId ? { eventID: eventId } : undefined)
    }

    return {
        event,
        viewItem,
        addToCart,
        removeFromCart,
        viewCart,
        addToFavorites,
        beginCheckout,
        purchase,
        login,
        search,
        callToAction
    };
}
